import { FC, useEffect } from 'react';

import { useRouter } from 'next/router';

import COOKIES from '~/constants/cookies';
import { useGetExtensionUserQuery, useRequestAuthorizationMutation } from '~/extension/schema';
import { authExpirationDays } from '~/helpers/auth';
import { useCookie } from '~/helpers/cookies';
import { useExtension } from '~/hooks/useExtension';
import { useExtensionConfig } from '~/hooks/useExtensionConfig';
import { useLoggedInUser } from '~/hooks/useLoggedInUser';
import { BrowserName } from '~/lib/extension-config';

import { DashboardPlaceholder } from './DashboardPlaceholder';
import { VerificationConfirmation } from './Settings/VerificationConfirmation';

/**
 * DashboardExtensionUserVerify is a component that is rendered around the dashboard content.
 * It will prompt the user to login with their extension user if there is one. If they are
 * already logged in the normal content will be rendered. If they are logged out and
 * the extension does not have a user the page will redirect to the login page.
 */
export const DashboardExtensionUserVerify: FC = ({ children }) => {
  const { loggedInAsRegistered, refreshUser, isLoggedIn, isLoggedInUserLoading } = useLoggedInUser();
  const router = useRouter();
  const { browserName } = useExtensionConfig();
  const { loading: statusLoading, isInstalled } = useExtension({
    pollForConnection: browserName === BrowserName.Firefox || browserName === BrowserName.Safari,
    attemptPollAmount: 5,
  });
  const shouldSkipUserQuery = statusLoading || !isInstalled;
  const { data, loading: userLoading } = useGetExtensionUserQuery({
    skip: shouldSkipUserQuery,
  });
  const [requestAuthorization, { loading: requestAuthLoading }] = useRequestAuthorizationMutation();
  const extensionUser = data?.me?.email
    ? {
        email: data.me.email,
        name: data.me.name,
      }
    : null;
  const isLoading = statusLoading || (userLoading && !shouldSkipUserQuery) || isLoggedInUserLoading;
  const [, setAuthCookie] = useCookie(COOKIES.TOKEN);

  const verifyExtensionUser = async () => {
    if (requestAuthLoading) {
      // already requested authorization
      return;
    }
    const resp = await requestAuthorization({});
    const token = resp.data?.requestAuthorization?.token;
    if (token) {
      setAuthCookie({ value: token, numDaysActive: authExpirationDays });
    }
    await refreshUser();
  };

  useEffect(() => {
    if (loggedInAsRegistered || isLoggedIn || isLoading || extensionUser) {
      return;
    }
    router.push('/log-in');
  }, [isLoading, router, loggedInAsRegistered, extensionUser, isLoggedIn]);

  if (isLoading) {
    return <DashboardPlaceholder />;
  }

  /**
   * having the extension user, but getting to this code block indicates that user
   * is logged into the extension but not the website, so we need to verify that.
   *
   * Clicking confirm allows the user to log into the website from the extension. We
   * currently only use the auth token, and not grab the refresh token. This is because
   * by running into this logic means the user is clearing cookies or somethings similar,
   * and there is probably little benefit to having the refresh token.
   *
   * We do an additional check if there user is not logged in here because we want to be able
   * to allow users already logged into the website to bypass this prompt.
   */
  if (!loggedInAsRegistered && extensionUser) {
    return (
      <VerificationConfirmation
        email={extensionUser.email}
        name={extensionUser.name ?? undefined}
        onConfirm={verifyExtensionUser}
      />
    );
  }

  /**
   * This just renders the content of the children no need to check anything else
   */
  if (loggedInAsRegistered || isLoggedIn) {
    return <>{children}</>;
  }

  /**
   * Render the loader here because the user should be navigated away at this
   * point, but something might render before the useEffect runs.
   */
  return <DashboardPlaceholder />;
};
